import { readNonZeroNaturalNumber } from "@outschool/environment";
import { FundingProgramAbbreviation } from "@outschool/funding-programs-shared";
import { GeoData } from "@outschool/iplookup-client";
import { BASE_LOCALE, I18nLocale } from "@outschool/localization";
import type { OsEnvironment } from "@outschool/platform";

type IpLookupInfoType = GeoData | undefined;

export type ExperimentAssignments = {
  loggedInUserExpUid: string | undefined;
  loggedOutUserExpUid: string | undefined;
  assignments: Record<string, string>;
};

export type OutschoolServer = {
  OUTSCHOOL_ENV: string;
  FEATURE_FLAGS: FeatureFlags;
  ENABLED_FEATURE_FLAGS: string[];
  APP_NAME: string;
  COMMIT_SHA: string;
  AWS_ACCOUNT_ID: string;
  STRIPE_PUBLISHABLE_KEY: string;
  STRIPE_CONNECTED_ACCOUNT_PUBLISHABLE_KEY?: string;
  STRIPE_WEBHOOK_SIG: string;
  SEGMENT_WRITE_KEY: string;
  AMPLITUDE_KEY: string;
  OPENAI_ADS_PIXEL_ID?: string;
  IS_PRERENDER_REQUEST: boolean;
  IS_BOT_REQUEST: boolean;
  LEARNER_HOSTNAME: string;
  APP_HOSTNAME: string;
  IS_LANGJS_APP: boolean;
  IS_REVIEW_APP: boolean;
  IPLOOKUP_API_KEY: string;
  IPLOOKUP_API_BASE_URL: string;
  EVENT_TRACKING_ENDPOINT: string;
  GBP_EXCHANGE_RATE: string;
  APPLE_SIGNIN_CLIENT_ID: string;
  FACEBOOK_APP_ID: string;
  GOOGLE_CLIENT_ID: string;
  ORDERS_EMAIL_PREVIEW_URL: string;
  CLASSROOM_EMAIL_PREVIEW_URL: string;
  ACCOUNT_MANAGEMENT_EMAIL_PREVIEW_URL: string;
  CONTENT_MODERATION_EMAIL_PREVIEW_URL: string;
  EXPERIMENTS_SERVICE_URL: string;
  EXPERIMENTS_API_KEY: string;
  EXPERIMENT_ASSIGNMENTS?: ExperimentAssignments;
  IPLOOKUP_INFO: GeoData | undefined;
  NOTIFICATIONS_SERVICE_URL: string;
  REFRESH_SESSION_TOKEN_REPEATER_DISABLED: string;
  TRANSLATION_SERVICE_URL: string;
  MUX_DATA_ENV_KEY: string;
  CLIENT_VERSION: number;
  CLASSWALLET_CALLBACK_URL: string;
  CLASSWALLET_VENDOR_ID: string;
  LOCALE: I18nLocale;
  UI_ANALYTICS_DEBUG: string;
  UI_ANALYTICS_DEV_MODE: string;
  UI_ANALYTICS_DISABLED_INTEGRATION_LIST: string;
  UI_ANALYTICS_EVENT_QUEUE_LIMIT: string;
  UI_ANALYTICS_LOAD_TIME_LIMIT: string;
  CLOUDFLARE_BOT_SCORE: string;
  CLOUDFLARE_BOT_VERIFIED: boolean;
  CLOUDFLARE_THREAT_SCORE: string;
  SEARCH_LISTINGS_RATE_LIMIT_PER_MINUTE: number;
  USE_PRODUCT_FEED_EXPORT_V2: boolean;
  GOOGLE_AUTH_DISALLOW_WEBVIEW: boolean;
  GQL_BLACKLIST: string[];
  GRANDFATHER_DATE: string;
  IS_READ_ONLY_MODE: boolean;
  PLATFORM_ENV: OsEnvironment;
  FF_ENABLE_FIRST_CC_USE_CHECK: boolean;
  TNS_POINTS_SUSPENSION_THRESHOLD: number;
  SEARCH_COUNSELOR_OPENAI_API_KEY: string;
  BACKGROUND_CHECK_EXPIRATION_MONTHS: number;
  OUTSCHOOL_CDN: string;
  TURNSTILE_SITEKEY: string | undefined;
  FF_USE_TURNSTILE_SIGNUP: boolean;
  UPCOMING_MAINTENANCE_DATE?: string;
};

// OUTSCHOOL_SERVER is set in marketplaceApp.tsx, and used by the client.
const OUTSCHOOL_SERVER: OutschoolServer = (global as any).OUTSCHOOL_SERVER;

// OUTSCHOOL_TEST_INIT is set in tests/init.js to signal we are in a test
const OUTSCHOOL_TEST_INIT: boolean = !!(global as any).OUTSCHOOL_TEST_INIT;

// isBrowser means, we should be in the browser.
// TODO: We should revisit this assumption when we work on server side
//       rendering
export const isBrowser = !!OUTSCHOOL_SERVER && !OUTSCHOOL_TEST_INIT;
export const isNode = !isBrowser;

export type Environment = "production" | "staging" | "development" | "test";
export const OUTSCHOOL_ENV = ((isNode && process.env.OUTSCHOOL_ENV) ||
  (isBrowser && OUTSCHOOL_SERVER.OUTSCHOOL_ENV) ||
  "development") as Environment;

export const AWS_ACCOUNT_ID = process.env.AWS_ACCOUNT_ID;

export const APP_NAME: string | undefined = isNode
  ? process.env.LANGJS_ENVNAME ?? "devel"
  : OUTSCHOOL_SERVER.APP_NAME;

export const COMMIT_SHA: string | undefined = isNode
  ? process.env.SOURCE_VERSION || process.env.GIT_COMMIT
  : OUTSCHOOL_SERVER.COMMIT_SHA;

export const isDebug =
  isNode &&
  process.execArgv &&
  (process.execArgv.indexOf("--debug-brk") !== -1 ||
    process.execArgv.indexOf("--debug") !== -1);

export function shouldUseSQS() {
  return !process.env.NO_TASK_QUEUES;
}

export const IPLOOKUP_API_KEY =
  (isNode && process.env.IPLOOKUP_API_KEY) ||
  (isBrowser && OUTSCHOOL_SERVER.IPLOOKUP_API_KEY) ||
  undefined;

export const isPrerenderRequest =
  isBrowser && !!OUTSCHOOL_SERVER.IS_PRERENDER_REQUEST;
export const isProduction = OUTSCHOOL_ENV === "production";
export const isStaging = OUTSCHOOL_ENV === "staging";
export const isTest = OUTSCHOOL_ENV === "test";
export const isDevelopment = OUTSCHOOL_ENV === "development";
export const isWebpackDevServer = isNode && !!process.env.WEBPACK_DEV_SERVER;
export const isLangjsApp =
  (isNode && !!process.env.LANGJS_ENVNAME) ||
  (isBrowser && OUTSCHOOL_SERVER.IS_LANGJS_APP) ||
  false;
export const isReviewApp =
  (isNode && process.env.IS_REVIEW_APP === "true") ||
  (isBrowser && OUTSCHOOL_SERVER.IS_REVIEW_APP) ||
  false;

export const GITLAB_ENV_NAME = isNode ? process.env.GITLAB_ENV_NAME : null;
export const IPLOOKUP_API_BASE_URL =
  (isNode && process.env.IPLOOKUP_API_BASE_URL) ||
  (isBrowser && OUTSCHOOL_SERVER.IPLOOKUP_API_BASE_URL) ||
  (isProduction ? "iplookup.app.outschool.com" : null) ||
  // review-apps default to staging version
  (isTest || isDevelopment ? null : "master-iplookup.review.outschool.dev");

export const redirectCalendars = isStaging || isProduction;

export const LEARNER_HOSTNAME = isBrowser
  ? OUTSCHOOL_SERVER.LEARNER_HOSTNAME
  : process.env.LEARNER_HOSTNAME;

export const APP_HOSTNAME = isBrowser
  ? OUTSCHOOL_SERVER.APP_HOSTNAME
  : process.env.APP_HOSTNAME;

export const MUX_CLASS_RECORDING_TOKEN_ID =
  process.env.MUX_CLASS_RECORDING_TOKEN_ID;
export const MUX_CLASS_RECORDING_TOKEN_SECRET =
  process.env.MUX_CLASS_RECORDING_TOKEN_SECRET;

export const MUX_DATA_ENV_KEY = isBrowser
  ? OUTSCHOOL_SERVER.MUX_DATA_ENV_KEY
  : process.env.MUX_DATA_ENV_KEY;

export const CLASSWALLET_DEFAULT_CHECKOUT_URL = isProduction
  ? "https://app.classwallet.com"
  : "https://app.classwallet.com"; // Currently both OS prod and staging point to CW prod :shrug:

export const LEARNER_TRACKING_HOSTNAME = isProduction
  ? "https://learner-tracking.outschool.com"
  : isStaging
  ? "https://learner-tracking.outschool.dev"
  : "http://learner-tracking.outschool.test";

// Used for media cdn
export const OUTSCHOOL_CDN =
  (isBrowser
    ? (global as any).OUTSCHOOL_SERVER?.OUTSCHOOL_CDN
    : process.env.OUTSCHOOL_CDN) ??
  (isProduction ? "cdn.outschool.com" : "cdn.outschool.dev");

export const EVENT_TRACKING_ENDPOINT = isProduction
  ? "https://events.outschool.com"
  : "https://events.outschool.dev";

export const SOCIAL_TOPICS_BASIC_AUTH_TOKEN =
  process.env.SOCIAL_TOPICS_BASIC_AUTH_TOKEN;

const TRUSTOS_PORT = process.env.TRUSTOS_PORT || 4010;
export const TRUSTOS_URL =
  process.env.TRUSTOS_URL ||
  (isProduction
    ? "http://server.trust-os.svc.cluster.local/graphql"
    : isStaging || isReviewApp
    ? "http://server.trust-os.svc.cluster.local/graphql"
    : `http://0.0.0.0:${TRUSTOS_PORT}/graphql`);

export const USER_ADMIN_URL =
  process.env.USER_ADMIN_URL ||
  (isProduction
    ? "https://user-admin.outschool.dev"
    : isStaging || isReviewApp
    ? "https://master-user-admin.review.outschool.dev"
    : "http://localhost:3010");

export const ADMIN_CREDIT_MAX_AMOUNT_DOLLARS = process.env
  .ADMIN_CREDIT_MAX_AMOUNT_DOLLARS
  ? Number.parseInt(process.env.ADMIN_CREDIT_MAX_AMOUNT_DOLLARS)
  : 50;
export const ADMIN_CREDIT_MIN_AMOUNT_DOLLARS = process.env
  .ADMIN_CREDIT_MIN_AMOUNT_DOLLARS
  ? Number.parseInt(process.env.ADMIN_CREDIT_MIN_AMOUNT_DOLLARS)
  : 1;
export const ADMIN_CREDIT_CONFIRMATION_PHRASE =
  process.env.ADMIN_CREDIT_CONFIRMATION_PHRASE || "trust";

export const ADMIN_CANCEL_CONFIRMATION_PASSWORD = "ESAcancel";

/**
 * GitLab feature flag names
 * @deprecated use a string literal instead of this enum
 */
export enum FeatureFlag {
  BuyerOrgAdminMode = "partners-buyer-org-admin-mode",
}

const FEATURE_FLAGS_SERVER_ONLY = {
  // --- Optional configuration variables for development ---
  // Override the learner app URL on review apps or during local development.
  LEARNER_HOSTNAME: process.env.LEARNER_HOSTNAME,
  // Allows review apps or local development to always show the experiment
  // override UI, even for non-admins or unauthenticated users.
  EXPERIMENT_OVERRIDE: process.env.FF_EXPERIMENT_OVERRIDE === "enabled",
  DISABLE_OPTIONAL_QUERIES: process.env.FF_DISABLE_OPTIONAL_QUERIES,
  ENABLE_PRERENDER: process.env.FF_ENABLE_PRERENDER,
  CURRENCY_ENROLLMENT: process.env.FF_CURRENCY_ENROLLMENT === "enabled",

  REFRESH_ERROR_REPORTING_TOGGLE:
    process.env.FF_REFRESH_ERROR_REPORTING_TOGGLE === "enabled",
  GHOST_CONTENT_KEY: process.env.GHOST_CONTENT_KEY,
  LOG_MISSING_REFRESH_TOKEN:
    isTest ||
    isDevelopment ||
    process.env.FF_LOG_MISSING_REFRESH_TOKEN === "enabled",
};

export const LOCALE = isBrowser ? OUTSCHOOL_SERVER.LOCALE : BASE_LOCALE;
export const ENABLE_LOCALE_PSEUDOLOCALIZATION = !isProduction; // Add more conditions as needed
export const DEFAULT_PSEUDOLOCALIZATION_REPEAT_COUNT = 2;

/** Environment variable feature flag values */
export const FEATURE_FLAGS = isBrowser
  ? OUTSCHOOL_SERVER.FEATURE_FLAGS
  : FEATURE_FLAGS_SERVER_ONLY;

type FeatureFlags = typeof FEATURE_FLAGS_SERVER_ONLY;

export const ENABLED_FEATURE_FLAGS = isBrowser
  ? OUTSCHOOL_SERVER.ENABLED_FEATURE_FLAGS
  : [];

export const clientVersion = isBrowser
  ? OUTSCHOOL_SERVER.CLIENT_VERSION
  : readNonZeroNaturalNumber(process.env.CLIENT_VERSION) ?? 435;

const batchSize = Number.parseInt(
  process.env.DATALOADER_BATCH_SIZE ?? "not a number",
  10,
);
export const DATALOADER_BATCH_SIZE = isNaN(batchSize) ? 20 : batchSize;

const STRIPE_TEST_PUBLISHABLE_KEY =
  "pk_test_516ULSSI4hzsXqJKOCwoZfuRFSfNWOrPE1pWVDKOwmGamBurl9YXQxEk9p0JJKrg9oepzu7TgRlAtIPh2lbOUfi6400XlAVgYSR";
const STRIPE_TEST_SECRET_KEY =
  "sk_test_516ULSSI4hzsXqJKOUDef1HLPeTTBaw7efrKr6ckBEG9uRF8cH614hIqBnjhzNEE97TvwefrtAOFOjQFK7IN1AuaY00cHoR92pE";

export const STRIPE_PUBLISHABLE_KEY =
  isProduction || isStaging
    ? isBrowser
      ? (global as any).OUTSCHOOL_SERVER.STRIPE_PUBLISHABLE_KEY
      : process.env.STRIPE_PUBLISHABLE_KEY
    : isTest
    ? undefined
    : STRIPE_TEST_PUBLISHABLE_KEY;

export const STRIPE_SECRET_KEY =
  isProduction || isStaging
    ? process.env.STRIPE_SECRET_KEY
    : isTest
    ? undefined
    : STRIPE_TEST_SECRET_KEY;

// prod uses current stripe key, staging uses connected account's sandbox key
// sandbox dashboard: https://dashboard.stripe.com/acct_1SqHVPImQDY1iPmn/test/workbench/overview
export const STRIPE_CONNECTED_ACCOUNT_KEY = isProduction
  ? process.env.STRIPE_SECRET_KEY
  : process.env.STRIPE_CONNECTED_ACCOUNT_KEY ?? undefined;

export const STRIPE_CONNECTED_ACCOUNT_PUBLISHABLE_KEY = isProduction
  ? isBrowser
    ? (global as any).OUTSCHOOL_SERVER.STRIPE_PUBLISHABLE_KEY
    : process.env.STRIPE_PUBLISHABLE_KEY
  : isBrowser
  ? (global as any).OUTSCHOOL_SERVER.STRIPE_CONNECTED_ACCOUNT_PUBLISHABLE_KEY ??
    undefined
  : process.env.STRIPE_CONNECTED_ACCOUNT_PUBLISHABLE_KEY ?? undefined;

/**
 * Setup your local webhook: https://docs.stripe.com/webhooks
 */
export const STRIPE_WEBHOOK_SIG = process.env.STRIPE_WEBHOOK_SIG || "";

export const STRIPE_API_VERSION = "2020-08-27";

export const SEGMENT_WRITE_KEY = isNode
  ? process.env.SEGMENT_WRITE_KEY
  : OUTSCHOOL_SERVER.SEGMENT_WRITE_KEY;

export const AMPLITUDE_KEY =
  (isNode && process.env.AMPLITUDE_KEY) ||
  (isBrowser && OUTSCHOOL_SERVER.AMPLITUDE_KEY) ||
  "";

export const OPENAI_ADS_PIXEL_ID = isBrowser
  ? OUTSCHOOL_SERVER.OPENAI_ADS_PIXEL_ID
  : process.env.OPENAI_ADS_PIXEL_ID;

export const GBP_EXCHANGE_RATE =
  (isNode && process.env.GBP_EXCHANGE_RATE) ||
  (isBrowser && OUTSCHOOL_SERVER.GBP_EXCHANGE_RATE) ||
  undefined;

/**
 * The service ID bundle to use to authorize.
 * You can add new service IDs here:
 * https://developer.apple.com/account/resources/identifiers/list/serviceId
 */
export const APPLE_SIGNIN_CLIENT_ID = isBrowser
  ? (global as any).OUTSCHOOL_SERVER.APPLE_SIGNIN_CLIENT_ID
  : process.env.APPLE_SIGNIN_CLIENT_ID;

/**
 * You can get this from:
 * https://developer.apple.com/account/#!/membership
 */
export const APPLE_DEVELOPER_TEAM_ID = process.env.APPLE_DEVELOPER_TEAM_ID;

/**
 * This should be the key ID of APPLE_SIGNIN_SECRET.
 * @see https://developer.apple.com/account/resources/authkeys/list
 */
export const APPLE_SIGNIN_KEY_ID = process.env.APPLE_SIGNIN_KEY_ID;

/**
 * The private key for APPLE_SIGNIN_KEY_ID
 * @see https://developer.apple.com/account/resources/authkeys/list
 */
export const APPLE_SIGNIN_SECRET = process.env.APPLE_SIGNIN_SECRET;

/**
 * The bundle id for the iOS Learner App
 */
export const IOS_LEARNER_APP_BUNDLE_ID = process.env.IOS_LEARNER_APP_BUNDLE_ID;

/**
 * The app id for Facebook.
 * @see https://developers.facebook.com/apps
 */
export const FACEBOOK_APP_ID = isBrowser
  ? OUTSCHOOL_SERVER.FACEBOOK_APP_ID
  : process.env.FACEBOOK_APP_ID;

export const GOOGLE_CLIENT_ID = isBrowser
  ? OUTSCHOOL_SERVER.GOOGLE_CLIENT_ID
  : process.env.GOOGLE_CLIENT_ID;

export const GOOGLE_CLIENT_ID_IOS = process.env.GOOGLE_CLIENT_ID_IOS;

export const GOOGLE_CLIENT_ID_ANDROID = process.env.GOOGLE_CLIENT_ID_ANDROID;

export const GOOGLE_CLIENT_SECRET = process.env.GOOGLE_CLIENT_SECRET;

export const ORDERS_EMAIL_PREVIEW_URL = isBrowser
  ? OUTSCHOOL_SERVER.ORDERS_EMAIL_PREVIEW_URL
  : process.env.ORDERS_EMAIL_PREVIEW_URL;

export const CLASSROOM_EMAIL_PREVIEW_URL = isBrowser
  ? OUTSCHOOL_SERVER.CLASSROOM_EMAIL_PREVIEW_URL
  : process.env.CLASSROOM_EMAIL_PREVIEW_URL;

export const ACCOUNT_MANAGEMENT_EMAIL_PREVIEW_URL = isBrowser
  ? OUTSCHOOL_SERVER.ACCOUNT_MANAGEMENT_EMAIL_PREVIEW_URL
  : process.env.ACCOUNT_MANAGEMENT_EMAIL_PREVIEW_URL;

export const CONTENT_MODERATION_EMAIL_PREVIEW_URL = isBrowser
  ? OUTSCHOOL_SERVER.CONTENT_MODERATION_EMAIL_PREVIEW_URL
  : process.env.CONTENT_MODERATION_EMAIL_PREVIEW_URL;

export const EXPERIMENTS_SERVICE_URL = isBrowser
  ? OUTSCHOOL_SERVER.EXPERIMENTS_SERVICE_URL
  : process.env.EXPERIMENTS_SERVICE_URL ||
    (isTest ? "http://experiments-test.url" : undefined);

export const EXPERIMENTS_API_KEY =
  (isBrowser
    ? OUTSCHOOL_SERVER.EXPERIMENTS_API_KEY
    : process.env.EXPERIMENTS_API_KEY) ?? "experiments-api-key";

const experimentAssignmentsMaxReqTimeMs = readNonZeroNaturalNumber(
  process.env.EXPERIMENT_ASSIGNMENTS_MAX_REQ_TIME_MS,
);
export const EXPERIMENT_ASSIGNMENTS_MAX_REQ_TIME_MS =
  experimentAssignmentsMaxReqTimeMs ?? 500;

export const EXPERIMENT_ASSIGNMENTS = OUTSCHOOL_SERVER?.EXPERIMENT_ASSIGNMENTS;

/**
 * Controls the number of days in advance that we'll update zoom meeting titles
 * to reflect changes in the activity title.
 */
export const LIMITED_DAYS_TO_UPDATE_ZOOM_MEETING_TITLES = Number.parseInt(
  process.env.LIMITED_DAYS_TO_UPDATE_ZOOM_MEETING_TITLES ?? "3",
);

// ClassWallet environment required on the front-end.
export const CLASSWALLET_CALLBACK_URL =
  (isBrowser
    ? OUTSCHOOL_SERVER.CLASSWALLET_CALLBACK_URL
    : process.env.CLASSWALLET_CALLBACK_URL) ||
  "gillin-classwallet-staging-app-website.review.outschool.dev";

export const CLASSWALLET_VENDOR_ID =
  (isBrowser
    ? OUTSCHOOL_SERVER.CLASSWALLET_VENDOR_ID
    : process.env.CLASSWALLET_VENDOR_ID) || "684988ebf08a2a9398053c31";

export const NOTIFICATIONS_SERVICE_URL =
  (isBrowser
    ? OUTSCHOOL_SERVER.NOTIFICATIONS_SERVICE_URL
    : process.env.NOTIFICATIONS_SERVICE_URL) ?? "http://localhost:3023";

export const TRANSLATION_SERVICE_URL = isBrowser
  ? OUTSCHOOL_SERVER.TRANSLATION_SERVICE_URL
  : process.env.TRANSLATION_SERVICE_URL ?? "http://translations.outschool.com";

export const REFRESH_SESSION_TOKEN_REPEATER_DISABLED = isBrowser
  ? !!OUTSCHOOL_SERVER.REFRESH_SESSION_TOKEN_REPEATER_DISABLED
  : process.env.REFRESH_SESSION_TOKEN_REPEATER_DISABLED;

/**
 * Whether or not it is running in gitlab CI
 *
 */
export const IS_GITLAB_CI = Boolean(process.env.GITLAB_CI);

export const isTestLocalEnv = isTest && !IS_GITLAB_CI;

/**
 * Throws an error if env variable doesn't exist in required environments
 * Default will **only** be provided in non-required environments
 *
 * Params:
 * - requiredEnvironments defaults to ['staging', 'production']
 */
export function getRequiredSecret(
  envVarName: string,
  defaultValue: string,
  requiredEnvironments: Environment[] = ["staging", "production"],
) {
  if (process.env[envVarName]) {
    return process.env[envVarName];
  }

  if (requiredEnvironments.includes(OUTSCHOOL_ENV)) {
    throw Error(
      `Required secret, (process.env.${envVarName}), was not defined`,
    );
  }

  return defaultValue;
}

export const PRERENDER_LATER = process.env.PRERENDER_LATER || false;

export const IPLOOKUP_INFO: IpLookupInfoType = isBrowser
  ? OUTSCHOOL_SERVER.IPLOOKUP_INFO
  : undefined;

/**
 * Sitemap Tasks
 */
export const GHOST_CONTENT_KEY = process.env.GHOST_CONTENT_KEY;

export const UI_ANALYTICS_DEBUG = isBrowser
  ? OUTSCHOOL_SERVER.UI_ANALYTICS_DEBUG
  : process.env.UI_ANALYTICS_DEBUG;
export const UI_ANALYTICS_DEV_MODE = isBrowser
  ? OUTSCHOOL_SERVER.UI_ANALYTICS_DEV_MODE
  : process.env.UI_ANALYTICS_DEV_MODE;
export const UI_ANALYTICS_DISABLED_INTEGRATION_LIST = isBrowser
  ? OUTSCHOOL_SERVER.UI_ANALYTICS_DISABLED_INTEGRATION_LIST
  : process.env.UI_ANALYTICS_DISABLED_INTEGRATION_LIST;
export const UI_ANALYTICS_EVENT_QUEUE_LIMIT = isBrowser
  ? OUTSCHOOL_SERVER.UI_ANALYTICS_EVENT_QUEUE_LIMIT
  : process.env.UI_ANALYTICS_EVENT_QUEUE_LIMIT;
export const UI_ANALYTICS_LOAD_TIME_LIMIT = isBrowser
  ? OUTSCHOOL_SERVER.UI_ANALYTICS_LOAD_TIME_LIMIT
  : process.env.UI_ANALYTICS_LOAD_TIME_LIMIT;

export const ZOOM_REST_API_TIMEOUT_MS = process?.env?.ZOOM_REST_API_TIMEOUT_MS
  ? Number.parseInt(process.env.ZOOM_REST_API_TIMEOUT_MS)
  : 10000;
export const ZOOM_WEBHOOK_SECRET_TOKENS =
  process?.env?.ZOOM_WEBHOOK_SECRET_TOKENS;

export const TRUSTOS_PAYOUT_CHECKPOINT_PARALLELISM = process.env
  .TRUSTOS_PAYOUT_CHECKPOINT_PARALLELISM
  ? Number.parseInt(process.env.TRUSTOS_PAYOUT_CHECKPOINT_PARALLELISM)
  : 5;

export const SUSPENDED_USER_CACHE_UPDATE_FREQUENCY_MS = process.env
  .SUSPENDED_USER_CACHE_UPDATE_FREQUENCY_MS
  ? Number.parseInt(process.env.SUSPENDED_USER_CACHE_UPDATE_FREQUENCY_MS)
  : 1000 * 60 * 10;

export const DUMMY_RECORDING_ID =
  process.env.DUMMY_RECORDING_ID ??
  "05f2a0ab-8a49-4783-8e41-92a16513959e/de9d9b51-b006-4195-9004-beb6f4d2cfda/1970-01-01T00:00.MP4";

/**
 * How long until the presigned URL expires.
 * User's browser must download and transcode the video within this time period.
 * The current value is 6 hours in seconds.
 * The maxiumum allowable value is 1 week in seconds.
 *
 * This also serves as the maximum recording duration that can be played back,
 * since the presigned URL would expire before playback finishes for longer recordings.
 * See `exceedsDurationLimit` in classRecordings.ts.
 *
 * See also: https://docs.aws.amazon.com/AmazonS3/latest/API/sigv4-query-string-auth.html
 */
export const S3_PRESIGNED_TIMEOUT_SECONDS =
  readNonZeroNaturalNumber(process.env.S3_PRESIGNED_TIMEOUT_SECONDS) ??
  60 * 60 * 6;

/**
 * How many topic chips the Quiz Writer GraphQL query returns (server-side cap).
 */
export const QUIZ_WRITER_TOPIC_SUGGESTIONS_LIMIT =
  readNonZeroNaturalNumber(process.env.QUIZ_WRITER_TOPIC_SUGGESTIONS_LIMIT) ??
  7;
export const QUIZ_WRITER_TOPIC_SUGGESTIONS_NEW_UNLOCKED_LIMIT =
  readNonZeroNaturalNumber(
    process.env.QUIZ_WRITER_TOPIC_SUGGESTIONS_NEW_UNLOCKED_LIMIT,
  ) ?? 2;

/** Default row count for `quizWriterTopicSearch` when no search text is provided. */
export const QUIZ_WRITER_TOPIC_SEARCH_SAMPLE_LIMIT =
  readNonZeroNaturalNumber(process.env.QUIZ_WRITER_TOPIC_SEARCH_SAMPLE_LIMIT) ??
  50;

/** Max rows returned when `quizWriterTopicSearch` has search text. */
export const QUIZ_WRITER_TOPIC_SEARCH_MATCH_LIMIT =
  readNonZeroNaturalNumber(process.env.QUIZ_WRITER_TOPIC_SEARCH_MATCH_LIMIT) ??
  100;

export const SEARCH_LISTINGS_RATE_LIMIT_PER_MINUTE: number | undefined =
  isBrowser
    ? OUTSCHOOL_SERVER.SEARCH_LISTINGS_RATE_LIMIT_PER_MINUTE
    : readNonZeroNaturalNumber(
        process.env.SEARCH_LISTINGS_RATE_LIMIT_PER_MINUTE,
      );

export const CPU_PROFILE_SERVER_RATIO = process.env.CPU_PROFILE_SERVER_RATIO;
export const CPU_PROFILE_GQL_QUERY_NAMES =
  process.env.CPU_PROFILE_GQL_QUERY_NAMES;
export const CPU_PROFILE_DELAY_THRESHOLD =
  process.env.CPU_PROFILE_DELAY_THRESHOLD;

export const USE_PRODUCT_FEED_EXPORT_V2 =
  process.env.USE_PRODUCT_FEED_EXPORT_V2 || false;

export const GOOGLE_AUTH_DISALLOW_WEBVIEW = isBrowser
  ? OUTSCHOOL_SERVER.GOOGLE_AUTH_DISALLOW_WEBVIEW
  : process.env.FF_GOOGLE_AUTH_DISALLOW_WEBVIEW === "enabled";

// A comma seperated list of 2 letter ISO country codes
export const REQUEST_3DS_COUNTRIES =
  process.env?.REQUEST_3DS_COUNTRIES?.split(",") ?? [];

export const USE_PAYOUTS_REVERSE_LOOKUP =
  isTest ||
  isDevelopment ||
  process.env.FF_USE_PAYOUTS_REVERSE_LOOKUP === "enabled";

export const CONSIDER_STOPPED_ALLOW_RESUME_ACTIVE =
  isTest ||
  isDevelopment ||
  process.env.CONSIDER_STOPPED_ALLOW_RESUME_ACTIVE === "enabled";

export const USE_V2_AUTH_EVERYWHERE =
  process.env.FF_USE_V2_AUTH_EVERYWHERE === "enabled";

export const INTERNAL_HOST_REDIRECT =
  process.env.INTERNAL_HOST_REDIRECT === "enabled";

// Enables sending errors to Sentry
// This should only be used for debugging purposes in non-prod environments
export const SENTRY_ENABLED = process.env.SENTRY_ENABLED === "true";
export const SENTRY_DSN = process.env.SENTRY_DSN;

// Waitlist Environment Variables

// Safety hatch if waitlist seats are affecting enrollments
export const UPDATE_WAITLIST_SEATS_ON_ENROLLMENT =
  process.env.UPDATE_WAITLIST_SEATS_ON_ENROLLMENT === "true";

export const WAITLIST_EMAILS_ENABLED =
  process.env.WAITLIST_EMAILS_ENABLED === "true";

/**
 * GQL MIDDLEWARE VARIABLES
 */
export const GQL_MIDDLEWARE_ENABLED =
  process.env.GQL_MIDDLEWARE_ENABLED === "enabled";
/**
 * Graphql operation name that will block until an email is confirmed
 */
export const GQL_EMAIL_CONFIRMATION_BLACKLIST =
  process.env.GQL_BLACKLIST?.split(",") || [];

/**
 * Users without a confirmed email that created an account before the
 * grandfather date will by-pass the email confirmed block above
 */
export const GRANDFATHER_DATE = process.env.GRANDFATHER_DATE;

const NEW_PRIVACY_TERMS_DEPLOYED_AT_RAW =
  process.env.NEW_PRIVACY_TERMS_DEPLOYED_AT;
export const NEW_PRIVACY_TERMS_DEPLOYED_AT =
  NEW_PRIVACY_TERMS_DEPLOYED_AT_RAW &&
  !Number.isNaN(Date.parse(NEW_PRIVACY_TERMS_DEPLOYED_AT_RAW))
    ? new Date(NEW_PRIVACY_TERMS_DEPLOYED_AT_RAW)
    : undefined;

export const IS_READ_ONLY_MODE = isBrowser
  ? OUTSCHOOL_SERVER.IS_READ_ONLY_MODE
  : process.env.READ_ONLY_MODE === "enabled";

/**
 * These vars control whether we log backend API calls to our Trust&Safety
 * log stream.
 */
export const TS_LOGGING_ENABLED =
  process.env.FF_TS_LOGGING_ENABLED === "enabled";
export const TS_LOGGING_QUERY_WHITELIST = new Set(
  (process.env.TS_LOGGING_QUERY_WHITELIST || "").split("|"),
);
export const TS_LOGGING_MUTATION_BLACKLIST = new Set(
  (process.env.TS_LOGGING_MUTATION_BLACKLIST || "").split("|"),
);

export const ROOTLY_API_KEY = process.env.ROOTLY_API_KEY || "";

/**
 * Enables an experiment to test the impact of requiring email confirmation to
 * purchase classes.
 */
export const SKIP_EMAIL_CONFIRMATION_FOR_PAYMENTS_EXP_ENABLED =
  isDevelopment ||
  isTest ||
  process.env.FF_SKIP_EMAIL_CONFIRMATION_FOR_PAYMENTS_EXP_ENABLED === "enabled";

export const LINE_CLIENT_ID_JA = process.env.LINE_CLIENT_ID_JA;
export const LINE_CLIENT_SECRET_JA = process.env.LINE_CLIENT_SECRET_JA;
export const LINE_CLIENT_ID_TW = process.env.LINE_CLIENT_ID_TW;
export const LINE_CLIENT_SECRET_TW = process.env.LINE_CLIENT_SECRET_TW;

export const KAKAO_CLIENT_ID = process.env.KAKAO_CLIENT_ID;
export const KAKAO_CLIENT_SECRET = process.env.KAKAO_CLIENT_SECRET;
// Client-side key with limited privileges for Kakao share SDK
export const KAKAO_JAVASCRIPT_KEY =
  process.env.KAKAO_JAVASCRIPT_KEY ?? "c0c16b591bc50e9c0d8c847bea368ebd";

export const STRIPE_FINGERPRINT_SHARING_LIMIT = isTest
  ? Number.parseInt(process.env.STRIPE_FINGERPRINT_SHARING_LIMIT || "2")
  : Number.parseInt(process.env.STRIPE_FINGERPRINT_SHARING_LIMIT || "0");

export const STRIPE_FINGERPRINT_BLOCKS_MAX_OTHER_CREDITS_COUNT =
  Number.parseInt(
    process.env.STRIPE_FINGERPRINT_BLOCKS_MAX_OTHER_CREDITS_COUNT || "0",
  );

// Default to enabled (non zero) in all envs but prod
export const TNS_POINTS_SUSPENSION_THRESHOLD = isBrowser
  ? OUTSCHOOL_SERVER.TNS_POINTS_SUSPENSION_THRESHOLD
  : Number.parseInt(
      process.env.TNS_POINTS_SUSPENSION_THRESHOLD || (isProduction ? "0" : "3"),
    );

export const TEACHER_SITE_API_KEY = process.env.TEACHER_SITE_API_KEY;

export const LOG_OTHER_ZOOM_WEBHOOKS = Boolean(
  process.env.LOG_OTHER_ZOOM_WEBHOOKS,
);

export const SEARCH_COUNSELOR_OPENAI_API_KEY =
  process.env.SEARCH_COUNSELOR_OPENAI_API_KEY;

export const BACKGROUND_CHECK_RENEWAL_ACTIVITY_REQUIREMENT_DAYS =
  Number.parseInt(
    process.env.BACKGROUND_CHECK_RENEWAL_ACTIVITY_REQUIREMENT_DAYS || "90",
  );

export const BACKGROUND_CHECK_EXPIRATION_MONTHS = isBrowser
  ? OUTSCHOOL_SERVER.BACKGROUND_CHECK_EXPIRATION_MONTHS
  : Number.parseInt(process.env.BACKGROUND_CHECK_EXPIRATION_MONTHS || "13");

export const FF_USE_DIFFERENT_CHECKR_PKG_FOR_RENEWALS =
  process.env.FF_USE_DIFFERENT_CHECKR_PKG_FOR_RENEWALS === "enabled";

/** How long should we wait before sending (some) new messages? */
export const MESSAGE_WAIT_PERIOD_MINUTES = Number.parseInt(
  process.env.MESSAGE_WAIT_PERIOD_MINUTES || "30",
);

export const WEBSITE_CSP_WHITELIST = process.env.WEBSITE_CSP_WHITELIST
  ? process.env.WEBSITE_CSP_WHITELIST.split(",")
  : [];

export const FF_ENABLE_ACCESS_CONTROL_ADMIN_ROLES =
  !isProduction ||
  process.env.FF_ENABLE_ACCESS_CONTROL_ADMIN_ROLES === "enabled";

export const MAX_MESSAGE_CONTENT_LENGTH = Number.parseInt(
  process.env.MAX_MESSAGE_CONTENT_LENGTH || "25000",
);

export const SUSPICIOUS_GIFT_CARD_LOOKBACK_DAYS = Number.parseInt(
  process.env.SUSPICIOUS_GIFT_CARD_LOOKBACK_DAYS || "0",
);

export const GIFT_CARD_PURCHASE_RATE_LIMIT_DAYS = Number.parseInt(
  process.env.GIFT_CARD_PURCHASE_RATE_LIMIT_DAYS || "1",
);

export const ESA_MEMBERSHIP_PROGRAMS = [] as FundingProgramAbbreviation[];

export const ENGAGELI_PILOT_CONFIG = process.env.ENGAGELI_PILOT_CONFIG;

export const TURNSTILE_SITEKEY = isBrowser
  ? OUTSCHOOL_SERVER.TURNSTILE_SITEKEY
  : process.env.TURNSTILE_SITEKEY;
export const DANGER_TEST_TURNSTILE_SITEKEY = "1x00000000000000000000AA";

export const TURNSTILE_SECRET_KEY = process.env.TURNSTILE_SECRET_KEY;
export const DANGER_TEST_TURNSTILE_SECRET_KEY =
  "1x0000000000000000000000000000000AA";

export const FF_USE_TURNSTILE_SIGNUP = isBrowser
  ? OUTSCHOOL_SERVER.FF_USE_TURNSTILE_SIGNUP
  : process.env.FF_USE_TURNSTILE_SIGNUP === "enabled";

export const APP_ATTEST_BUNDLE_IDENTIFIER =
  process.env.APP_ATTEST_BUNDLE_IDENTIFIER;
export const APP_ATTEST_TEAM_IDENTIFIER =
  process.env.APP_ATTEST_TEAM_IDENTIFIER;
export const FF_USE_APP_ATTEST = process.env.FF_USE_APP_ATTEST === "enabled";

export const FF_USE_PLAY_INTEGRITY =
  process.env.FF_USE_PLAY_INTEGRITY === "enabled";
export const PLAY_INTEGRITY_PROJECT_ID = process.env.PLAY_INTEGRITY_PROJECT_ID;
export const PLAY_INTEGRITY_PACKAGE_NAME =
  process.env.PLAY_INTEGRITY_PACKAGE_NAME || "com.outschool.learner";
export const GOOGLE_APPLICATION_CREDENTIALS_JSON =
  process.env.GOOGLE_APPLICATION_CREDENTIALS_JSON;

export const OUTSCHOOL_SECURED_FILE_BUCKET =
  process.env.OUTSCHOOL_SECURED_FILE_BUCKET || "outschool-secured-filestack";

export const CLASS_RECORDINGS_BUCKET_NAME =
  process.env.CLASS_RECORDINGS_BUCKET_NAME ||
  (isProduction
    ? "outschool-class-recordings"
    : "outschool-class-recordings-test");

export const FF_ENABLE_ACTIVITY_RE_REVIEW =
  Boolean(process.env.FF_ENABLE_ACTIVITY_RE_REVIEW) || isTest;

export const FF_ENABLE_LEARNER_HEALTH_DATA_CONTROLS =
  process.env.FF_ENABLE_LEARNER_HEALTH_DATA_CONTROLS === "enabled";

export const TEST_MAGIC_LOGIN_LINK_LOCALLY =
  process.env.TEST_MAGIC_LOGIN_LINK_LOCALLY === "enabled";

export const UPCOMING_MAINTENANCE_DATE = isBrowser
  ? OUTSCHOOL_SERVER.UPCOMING_MAINTENANCE_DATE
  : process.env.UPCOMING_MAINTENANCE_DATE;

export const QUIZ_WRITER_THINKING_MODEL_ARN =
  process.env.QUIZ_WRITER_THINKING_MODEL_ARN ||
  "us.anthropic.claude-sonnet-4-20250514-v1:0";
export const QUIZ_WRITER_SCANNING_MODEL_ARN =
  process.env.QUIZ_WRITER_SCANNING_MODEL_ARN ||
  "us.anthropic.claude-3-haiku-20240307-v1:0";
export const QUIZ_WRITER_MODEL_COSTS_PER_TOKEN = process.env
  .QUIZ_WRITER_MODEL_COSTS_PER_TOKEN
  ? JSON.parse(process.env.QUIZ_WRITER_MODEL_COSTS_PER_TOKEN)
  : {
      [QUIZ_WRITER_THINKING_MODEL_ARN]: {
        input: 3 / 1000000,
        output: 15 / 1000000,
      },
      [QUIZ_WRITER_SCANNING_MODEL_ARN]: {
        input: 0.25 / 1000000,
        output: 1.25 / 1000000,
      },
    };
/** Omni embed SSO signing secret. When set, Detailed Stats can load teacher Omni dashboards via signed SSO. */
export const OMNI_EMBED_SECRET = process.env.OMNI_EMBED_SECRET;

export const PERSONA_TEMPLATE_KEY = process.env.PERSONA_TEMPLATE_KEY;
export const PERSONA_CONSOLIDATE_TEMPLATE_ID =
  process.env.PERSONA_CONSOLIDATE_TEMPLATE_ID;
export const PERSONA_BASIC_BACKGROUND_CHECK_INQUIRY_TEMPLATE_ID =
  process.env.PERSONA_BASIC_BACKGROUND_CHECK_INQUIRY_TEMPLATE_ID;
export const PERSONA_PREMIUM_BACKGROUND_CHECK_INQUIRY_TEMPLATE_ID =
  process.env.PERSONA_PREMIUM_BACKGROUND_CHECK_INQUIRY_TEMPLATE_ID;
